'use client';

import React, { useState } from 'react';
import { isBrowser } from '@/sharedTypes/browserUtils';

// Dynamic import of RecordRTC
import type RecordRTC from 'recordrtc';
let RecordRTCModule: typeof RecordRTC | null = null;
// eslint-disable-next-line
let isModuleLoading = true;

if (isBrowser()) {
  import('recordrtc')
    .then((module) => {
      RecordRTCModule = module.default;
      isModuleLoading = false;
    })
    .catch((err) => {
      console.error('Failed to load RecordRTC:', err);
      isModuleLoading = false;
    });
}

import fixWebmDuration from 'fix-webm-duration';

import './DebugRecorder.css';
import { observer } from 'mobx-react';
import DebugModelEntity from './DebugModel';
import { apiSwagger } from '../../shared/api';
import { ManagerEntity } from '@/shared/globalFunctions';
import { gMsg, notifyStatus } from '@/sharedTypes/coreTypes';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import { dpAssert } from '@/sharedTypes/helpers';
import toast from '../ui/custom-toast';
import { useTranslation } from 'react-i18next';
import { NEXT_PUBLIC_API_URL_CLIENT_SIDE } from '@/shared/envs';

const saveFile = false;

type RecorderGlobal = {
  recorder: RecordRTC | null;
};
const recorderGlobal: RecorderGlobal = {
  recorder: null,
};

const mimeType = 'video/webm';
const fileExtension = 'webm';

type RecordingState = 'idle' | 'recording' | 'stopped';

export const DebugRecorder = observer(() => {
  // Use ManagerEntity directly (global singleton) instead of useManagerEntity hook
  // because BottomLine doesn't have access to the manager context
  const player = ManagerEntity.player;
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordStartedDate, setRecordStartingDate] = useState<number>(0);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);

  const refreshUUIDs = () => {
    DebugModelEntity.refreshUUIDS();
  };

  // idle → Start recording
  if (recordingState === 'idle') {
    return (
      <button
        className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors border border-red-300'
        onClick={async () => {
          console.log('[DebugRecorder] Starting recording clicked');
          if (!player) {
            console.error('[DebugRecorder] Player not available');
            toast({
              title: t('toast.error_title', 'Error'),
              description: t('debug.player_not_ready', 'Player not ready. Please wait for sample to load.'),
              type: 'error',
            });
            return;
          }
          if (!RecordRTCModule) {
            console.error('[DebugRecorder] RecordRTC module not loaded');
            toast({
              title: t('toast.error_title', 'Error'),
              description: t('debug.recordrtc_not_loaded', 'Recording module not loaded. Please refresh the page.'),
              type: 'error',
            });
            return;
          }
          console.log('[DebugRecorder] Starting recording...');
          const success = await startRecording(setStream, recorderGlobal);
          if (success) {
            setRecordStartingDate(Date.now());
            setRecordingState('recording');
          }
        }}
      >
        <span className='w-2 h-2 rounded-full bg-red-500'></span>
        {t('debug.start_recording', 'Rec')}
      </button>
    );
  }

  // recording → Stop (but don't save yet)
  if (recordingState === 'recording') {
    return (
      <button
        style={{ backgroundColor: '#dc2626', color: 'white', opacity: 1, visibility: 'visible' }}
        className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-red-700 transition-colors'
        onClick={async () => {
          const recorder = recorderGlobal.recorder;
          if (!recorder) {
            console.error('[DebugRecorder] No recorder available');
            setRecordingState('idle');
            toast({
              title: t('toast.error_title', 'Error'),
              description: t('debug.no_recorder', 'No recorder available.'),
              type: 'error',
            });
            return;
          }

          // Stop recording and get the blob, but don't upload yet
          console.log('[DebugRecorder] Stopping recording...');

          // Use callback-based stopRecording to ensure blob is ready
          recorder.stopRecording(async () => {
            console.log('[DebugRecorder] Recording stopped, getting blob...');
            const blob = recorder.getBlob();
            console.log('[DebugRecorder] Blob:', blob, 'size:', blob?.size);

            // Stop media tracks
            if (stream) {
              stream.getTracks().forEach((track) => track.stop());
              setStream(null);
            }

            if (blob && blob.size > 0) {
              // Fix duration and store blob for later
              const duration = Date.now() - recordStartedDate;
              console.log('[DebugRecorder] Fixing webm duration, duration:', duration);
              fixWebmDuration(blob, duration, (fixedBlob) => {
                console.log('[DebugRecorder] Duration fixed, blob size:', fixedBlob.size);
                setPendingBlob(fixedBlob);
                setRecordingState('stopped');
                toast({
                  title: t('toast.info_title', 'Info'),
                  description: t('debug.recording_stopped', 'Recording stopped. Click Save to upload or Discard to cancel.'),
                  type: 'info',
                });
              });
            } else {
              console.error('[DebugRecorder] No blob or empty blob');
              setRecordingState('idle');
              toast({
                title: t('toast.error_title', 'Error'),
                description: t('debug.no_recording_data', 'No recording data available.'),
                type: 'error',
              });
            }
          });
        }}
      >
        <span className='w-2 h-2 rounded-full bg-white'></span>
        {t('debug.stop_recording', 'Stop')}
      </button>
    );
  }

  // stopped → Save or Discard
  return (
    <div className='flex items-center gap-1'>
      <button
        className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors'
        onClick={async () => {
          if (pendingBlob && player?.eventsRecorder?.debugData) {
            ManagerEntity.spinnerMsg = 'Saving debug info...';

            // Save debug events and get the UUID from server response
            const uuid = await saveDebugData(player.eventsRecorder.debugData, t);

            if (!uuid) {
              ManagerEntity.spinnerMsg = '';
              toast({
                title: t('toast.error_title', 'Error'),
                description: t('debug.debug_data_save_failed', 'Failed to save debug data.'),
                type: 'error',
              });
              // Reset state even on failure so user can try again
              setPendingBlob(null);
              setRecordingState('idle');
              return;
            }

            // Save video with the UUID from server
            const saved = await saveVideo(pendingBlob, uuid);

            if (saved) {
              toast({
                title: t('toast.success_title', 'Success'),
                description: t('debug.recording_saved', 'Recording saved successfully.'),
                type: 'success',
              });
              refreshUUIDs();
            } else {
              toast({
                title: t('toast.error_title', 'Error'),
                description: t('debug.recording_save_failed', 'Failed to save recording.'),
                type: 'error',
              });
            }

            ManagerEntity.spinnerMsg = '';
            setPendingBlob(null);
            setRecordingState('idle');
          }
        }}
      >
        {t('debug.save_recording', 'Save')}
      </button>
      <button
        className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors'
        onClick={() => {
          setPendingBlob(null);
          setRecordingState('idle');
          toast({
            title: t('toast.info_title', 'Info'),
            description: t('debug.recording_discarded', 'Recording discarded.'),
            type: 'info',
          });
        }}
      >
        {t('debug.discard_recording', 'Discard')}
      </button>
    </div>
  );
});

const startRecording = async (
  setStream: React.Dispatch<React.SetStateAction<MediaStream | null>>,
  recorderGlobal: any
): Promise<boolean> => {
  console.log('[DebugRecorder] Starting recording process...');
  const startTime = performance.now();

  if (!RecordRTCModule) {
    console.error('[DebugRecorder] RecordRTC module not loaded');
    return false;
  }

  try {
    console.log('[DebugRecorder] Requesting display media...');
    const videoStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });
    console.log('[DebugRecorder] Display media obtained');
    console.log('[DebugRecorder] Video tracks:', videoStream.getVideoTracks().length);
    console.log('[DebugRecorder] Audio tracks from screen:', videoStream.getAudioTracks().length);

    // Try to get microphone, but don't fail if unavailable
    let micStream: MediaStream | null = null;
    try {
      console.log('[DebugRecorder] Requesting microphone access...');
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      console.log('[DebugRecorder] Microphone access granted');
    } catch (micError) {
      console.warn('[DebugRecorder] Microphone not available, continuing without it:', micError);
    }

    // Mix audio streams if we have any
    const screenAudioTracks = videoStream.getAudioTracks();
    if (screenAudioTracks.length > 0 || micStream) {
      console.log('[DebugRecorder] Setting up audio context...');
      const audioContext = new AudioContext();
      const dest = audioContext.createMediaStreamDestination();

      // Add screen audio if available
      if (screenAudioTracks.length > 0) {
        const audioIn_01 = audioContext.createMediaStreamSource(videoStream);
        audioIn_01.connect(dest);
        // Remove original screen audio track
        videoStream.removeTrack(screenAudioTracks[0]);
      }

      // Add microphone if available
      if (micStream) {
        const audioIn_02 = audioContext.createMediaStreamSource(micStream);
        audioIn_02.connect(dest);
      }

      // Add mixed audio to video stream
      videoStream.addTrack(dest.stream.getAudioTracks()[0]);
      console.log('[DebugRecorder] Audio streams mixed and set up');
    } else {
      console.log('[DebugRecorder] No audio sources available, recording video only');
    }

    setStream(videoStream);

    console.log('[DebugRecorder] Initializing RecordRTC...');
    const r = new RecordRTCModule(videoStream, {
      type: 'video',
      timeSlice: 1000,
      mimeType: 'video/webm;codecs=vp9',
    });

    r.startRecording();
    recorderGlobal.recorder = r;
    console.log('[DebugRecorder] Recording started successfully');

    const duration = performance.now() - startTime;
    console.log(`[DebugRecorder] Total setup time: ${duration.toFixed(2)}ms`);
    return true;
  } catch (error: any) {
    console.error('[DebugRecorder] Error during recording setup:', error);
    // Handle errors
    function showError(msg: string) {
      console.error(`[DebugRecorder] ${msg}`);
    }
    if (error.name === 'NotAllowedError') {
      showError('User denied permission for camera and/or microphone.');
    } else if (
      error.name === 'NotFoundError' ||
      error.name === 'DevicesNotFoundError'
    ) {
      showError('No camera and/or microphone found.');
    } else if (
      error.name === 'NotReadableError' ||
      error.name === 'TrackStartError'
    ) {
      showError('Camera and/or microphone is already in use or unavailable.');
    } else if (
      error.name === 'OverconstrainedError' ||
      error.name === 'ConstraintNotSatisfiedError'
    ) {
      const msg =
        'The constraints you specified could not be satisfied by the available devices.';
      showError(msg);
    } else if (error.name === 'InvalidStateError') {
      const msg =
        'Не удалось  получить доступ к аудио, пожалуйста выберите захват всего экрана и включите галочку предоставить доступ к аудиоданным';
      showError(msg);
    } else {
      const msg = 'Error accessing camera and/or microphone:';
      showError(msg);
    }
    return false;
  }
};

async function stopRecording(
  recorderGlobal: RecorderGlobal,
  setStream: React.Dispatch<React.SetStateAction<MediaStream | null>>,
  stream: MediaStream | null,
  refreshUUIDs: () => void,
  player: any | undefined,
  recordStartedDate: number,
  t: any
) {
  console.log('[DebugRecorder] Stopping recording process...');
  const startTime = performance.now();

  ManagerEntity.spinnerMsg = 'saving debug info...';

  const RTCRecorder = recorderGlobal.recorder;

  if (RTCRecorder) {
    let duration: any = null;
    if (recordStartedDate) {
      duration = Date.now() - recordStartedDate;
      console.log(`[DebugRecorder] Recording duration: ${duration}ms`);
    }

    console.log('[DebugRecorder] Stopping recorder...');
    await RTCRecorder.stopRecording();

    if (player?.eventsRecorder.debugData) {
      console.log('[DebugRecorder] Saving debug data...');
      await saveDebugData(player?.eventsRecorder.debugData, t);
    }

    console.log('[DebugRecorder] Getting recording blob...');
    const blob = await RTCRecorder.getBlob();

    if (blob) {
      console.log(`[DebugRecorder] Blob size: ${blob?.size} bytes`);
      if (player) {
        if (recordStartedDate) {
          if (duration) {
            console.log('[DebugRecorder] Fixing webm duration...');
            fixWebmDuration(blob, duration, async function (fixedBlob) {
              console.log('[DebugRecorder] Saving video...');
              const saved = await saveVideo(
                fixedBlob,
                player.eventsRecorder.debugData.uuid
              );

              if (stream) {
                console.log('[DebugRecorder] Stopping media tracks...');
                stream.getTracks().forEach((track) => track.stop());
                setStream(null);
              }

              if (saveFile) {
                console.log('[DebugRecorder] Saving file locally...');
                const file = new File(
                  [fixedBlob],
                  'file-name.' + fileExtension,
                  {
                    type: mimeType,
                  }
                );
                dpAssert(RecordRTCModule, 'RecordRTCModule is null');
                RecordRTCModule.invokeSaveAsDialog(file);
              }

              refreshUUIDs();
              if (saved) {
                console.log('[DebugRecorder] Recording saved successfully');
                ManagerEntity.gMsg = new gMsg(
                  'Recording saved',
                  notifyStatus.success
                );
              } else {
                console.error('[DebugRecorder] Failed to save recording');
                ManagerEntity.gMsg = new gMsg(
                  'Failed to save recording',
                  notifyStatus.failed
                );
              }
              ManagerEntity.spinnerMsg = '';

              const totalDuration = performance.now() - startTime;
              console.log(
                `[DebugRecorder] Total stop process time: ${totalDuration.toFixed(
                  2
                )}ms`
              );
            });
          }
        }
      } else {
        console.error(
          '[DebugRecorder] Player not found when trying to save video'
        );
        ManagerEntity.gMsg = new gMsg(
          'Player not found when tried to save video',
          1
        );
        ManagerEntity.spinnerMsg = '';
      }
    } else {
      console.error('[DebugRecorder] Video blob not saved, there is no blob');
      ManagerEntity.gMsg = new gMsg("Video blob not saved, there' no blob", 1);
      ManagerEntity.spinnerMsg = '';
    }
  } else {
    console.error('[DebugRecorder] Recorder is undefined');
    ManagerEntity.gMsg = new gMsg('Recorded is undefined', 1);
    ManagerEntity.spinnerMsg = '';
  }
}

function serializeObject(obj: any) {
  const result: any = {
    ...obj,
  };
  result.events = {};
  for (const key in obj.events) {
    const k = key.toString();
    result.events[k] = obj.events[key];
  }
  return result;
}

async function saveVideo(blob: any, uuid: string) {
  console.log('[DebugRecorder] Starting video save process...');
  console.log('[DebugRecorder] UUID for video:', uuid);
  console.log('[DebugRecorder] Blob:', blob, 'size:', blob?.size);
  const startTime = performance.now();

  if (!uuid) {
    console.error('[DebugRecorder] UUID is empty or undefined!');
    return false;
  }

  if (blob) {
    const formData = new FormData();
    formData.append('file', blob, `${uuid}.webm`);
    formData.append('uuid', uuid);

    // Log FormData contents
    console.log('[DebugRecorder] FormData entries:');
    for (const [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value);
    }

    console.log('[DebugRecorder] Sending video to server...');

    // Use direct fetch instead of Swagger API to ensure FormData is sent correctly
    try {
      // Get auth token first
      let headers: HeadersInit = {};
      try {
        const tokenResponse = await fetch('/api/auth/token');
        if (tokenResponse.ok) {
          const { token } = await tokenResponse.json();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        }
      } catch (e) {
        console.warn('[DebugRecorder] Could not get auth token:', e);
      }

      const response = await fetch(`${NEXT_PUBLIC_API_URL_CLIENT_SIDE}/debugVideo`, {
        method: 'POST',
        body: formData,
        headers,
        credentials: 'include',
      });

      console.log('[DebugRecorder] Video response status:', response.status);

      if (response.ok) {
        console.log('[DebugRecorder] Video saved successfully');
        const duration = performance.now() - startTime;
        console.log(`[DebugRecorder] Video save time: ${duration.toFixed(2)}ms`);
        return true;
      } else {
        const errorText = await response.text();
        console.error('[DebugRecorder] Failed to save video:', errorText);
        return false;
      }
    } catch (error) {
      console.error('[DebugRecorder] Error uploading video:', error);
      return false;
    }
  }
  return false;
}

// Returns the UUID if successful, null otherwise
export async function saveDebugData(debugData: any, t: any): Promise<string | null> {
  console.log('[DebugRecorder] Starting debug data save process...');
  const startTime = performance.now();

  const sDebugData: any = serializeObject(debugData);
  try {
    const isEmpty = (obj: any) => {
      return Object.keys(obj).length === 0;
    };
    if (!isEmpty(debugData.events)) {
      console.log('[DebugRecorder] Sending debug data to server...');
      console.log('[DebugRecorder] Debug data to send:', sDebugData);
      const response = await apiSwagger.debugEvents.debugEventsCreate(
        sDebugData
      );
      console.log('[DebugRecorder] Debug data save response:', response);
      console.log('[DebugRecorder] Response ok:', response.ok);
      console.log('[DebugRecorder] Response data:', response.data);
      console.log('[DebugRecorder] Response error:', response.error);

      if (response.ok && response.data?.uuid) {
        const uuid = response.data.uuid;
        console.log('[DebugRecorder] Debug data saved successfully, UUID:', uuid);

        // Trigger event only after successful save
        const interval = setInterval(() => {
          console.log('[DebugRecorder] Triggering debug data saved event...');
          triggerEvent('debugDataSaved', uuid);
        }, 1000);

        setTimeout(() => {
          clearInterval(interval);
        }, 10000);

        toast({
          title: t('toast.success_title'),
          description: `${t('toast.debug_data_saved_success')}: UUID - ${uuid}`,
          type: 'success',
          duration: 20000,
        });
        const duration = performance.now() - startTime;
        console.log(
          `[DebugRecorder] Debug data save time: ${duration.toFixed(2)}ms`
        );
        return uuid;
      } else {
        console.error('[DebugRecorder] Debug data failed to save');
        console.error('[DebugRecorder] Error details:', response.error || 'Unknown error');
        toast({
          title: t('toast.error_title'),
          description: t('toast.debug_data_save_error') + ': ' + (typeof response.error === 'object' ? JSON.stringify(response.error) : response.error || 'Unknown error'),
          type: 'error',
        });
        return null;
      }
    } else {
      console.warn('[DebugRecorder] Attempted to save empty debug data');
      toast({
        title: t('toast.warning_title'),
        description: t('toast.debug_no_data_warning'),
        type: 'warning',
      });
      return null;
    }
  } catch (error) {
    console.error('[DebugRecorder] Error saving debug data:', error);
    toast({
      title: t('toast.error_title'),
      description: `${t('toast.debug_save_error_desc')}: ${error}`,
      type: 'error',
    });
    return null;
  }
}
